import { FileText, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { KpiTile } from '@/components/data/KpiTile'
import { DashboardSkeleton } from '@/components/feedback/Skeleton'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { useSession } from '@/features/auth/api/useSession'
import { useDashboard } from '@/features/dashboard/api/useDashboard'
import { AttentionList } from '@/features/dashboard/components/AttentionList'
import { CounterStrip } from '@/features/dashboard/components/CounterStrip'
import { ErrorState } from '@/components/feedback/ErrorState'
import { toCasePayload, useCreateCase } from '@/features/cases/api/useCase'
import { CaseFormSheet } from '@/features/cases/components/CaseFormSheet'
import { useFormat } from '@/i18n/useFormat'
import { formatNumber } from '@/lib/format'
import { canSeeCrossCaseFinance } from '@/lib/navigation'
import type { DashboardSummary, Money } from '@/types/domain'

/** Which greeting to use. Both catalogues supply three; the boundaries live
 *  here because Russian and English divide the day differently and neither
 *  split belongs in a translation string. */
function greetingKey(hour: number): 'morning' | 'afternoon' | 'evening' {
  if (hour < 12) return 'morning'
  if (hour < 17) return 'afternoon'
  return 'evening'
}

/* The three money tiles, in the order the mockup places them.
 *
 * `profit` rather than `forecastProfit`: FR-51 AC 2 makes the tile one figure
 * over two bases — a financially closed case contributes what it finally earned
 * and every other case what it is forecast to earn — and the server composes
 * that as `counters.profit`. `forecastProfit` is still on the response as that
 * figure's open-cases component; it is not a tile.
 *
 * Rendered from a list rather than written out three times because a tile stops
 * being pending individually, which is a change this list makes obvious. */
const MONEY_TILES = ['turnover', 'profit', 'toCollect'] as const

type MoneyTileKey = (typeof MONEY_TILES)[number]

/** The story that unblocks a tile, or undefined if the server sent the figure.
 *
 *  Reading the reason off the RESPONSE rather than hard-coding it here: the
 *  server owns what it could not compute, and a copy of that list in the client
 *  is a copy that goes stale the moment a story lands. */
function storyFor(
  pending: DashboardSummary['pending'],
  key: MoneyTileKey,
): string | undefined {
  return pending.find((p) => p.key === key)?.story
}

/** Where a tile's figure drills through to (UX-DR-10).
 *
 *  EMPTY TODAY, AND THAT IS THE FINDING RATHER THAN A GAP IN IT. UX-DR-10 asks
 *  for every figure to open the filtered list behind it, and it is explicit
 *  about the harder half: "the tile and its destination list are computed from
 *  the same predicate and the same rate basis, so the list always sums to the
 *  figure that was clicked". A link that lands on a list computed differently is
 *  worse than no link — it teaches the founder that the figures disagree, which
 *  is the doubt this whole surface exists to remove.
 *
 *  Neither destination exists yet, and each is one specific missing thing:
 *
 *    · `profit` has no list at all. There is no per-case profit view to land on.
 *    · `toCollect` and `toPay` would land on Finance → Balances and Aging, both
 *      of which are still served from `internal/showcase` fixtures. Linking a
 *      real figure to invented rows is the exact failure above.
 *    · The active-case COUNTER would land on `/cases`, which shows every case
 *      the caller may see while the counter counts only IN PROCESS ones. The
 *      case list does not read a status from the URL, so the link cannot narrow
 *      it.
 *
 *  The affordance itself is wired: `KpiTile` takes a `link` and renders it, and
 *  the attention rows already drill through to a single record — where the
 *  destination is the record itself and therefore cannot disagree. Populating
 *  this map is the whole of turning the tiles on, once a destination can sum. */
const TILE_HREFS: Partial<Record<MoneyTileKey, string>> = {}

/** The figure a tile renders, or undefined when the response carries none.
 *
 *  `turnover` has no field on `counters` at all — it is named in `pending` and
 *  nothing computes it (Story 5.5 issues the invoices it sums). Returning
 *  undefined rather than indexing for it is what keeps that a compile-time fact:
 *  the day turnover becomes real, this switch is where the field is added, and
 *  until then no expression in this file can pretend to read one. */
function figureFor(
  counters: DashboardSummary['counters'],
  key: MoneyTileKey,
): Money | undefined {
  switch (key) {
    case 'profit':
      return counters.profit
    case 'toCollect':
      return counters.toCollect
    case 'turnover':
      return undefined
  }
}

/** A tile whose figure the server could not compute yet.
 *
 *  It states the absence rather than rendering an empty card, for the reason
 *  UX-DR-17 gives about empty states generally: a blank box is read as a
 *  loading failure, and a zero is read as data. Naming the story turns "broken"
 *  into "not yet", which is the difference between a bug report and a roadmap. */
function PendingTile({ label, story }: { label: string; story?: string }) {
  const { t } = useTranslation()
  if (!story) return null
  return (
    <Card className="p-[18px]">
      <span className="text-text-muted text-[13px] leading-tight font-normal">
        {label}
      </span>
      <p className="text-text-muted mt-3 text-[13px] leading-[1.5] font-light">
        {t('dashboard.pending.description', { story })}
      </p>
    </Card>
  )
}

/** A tile whose figure the server DID compute.
 *
 *  Renders nothing when the amount is absent, which is not the same as
 *  rendering a zero: the server omits a figure it could not compute, and the
 *  tile it would have filled is left to PendingTile — or, when the server named
 *  no story either, to nothing at all. An empty slot is honest; a zero is a
 *  number the founder would act on.
 *
 *  BUILT ON KpiTile rather than on a Card of its own. That is the fix for the
 *  orphaned-component finding and it is not just tidiness: `KpiTile` already
 *  carries the `link` UX-DR-10 needs, already renders it in the header where the
 *  mockup puts it, and is already the tile the quotes surface uses
 *  (PipelineStatistics) — so the dashboard and the pipeline now agree on what a
 *  KPI looks like instead of each having a private one. `KpiCard`, the wrapper
 *  that used to sit between them, is deleted: it composed a generic `KpiTile`
 *  domain object the server does not send and, per UX-DR-10's second clause,
 *  must not — a generic tile list is precisely how a figure and its destination
 *  come to be computed from different predicates. */
function MoneyTile({
  label,
  amount,
  href,
  hrefLabel,
}: {
  label: string
  amount?: Money
  href?: string
  hrefLabel: string
}) {
  if (!amount) return null
  return (
    <KpiTile
      label={label}
      value={formatNumber(amount.amount, 2)}
      unit={amount.currency}
      link={href ? { to: href, label: hrefLabel } : undefined}
    />
  )
}

/* The founder dashboard — the COMFORTABLE regime. The founder reads five
 * numbers over morning coffee and needs them to land in seconds, so this
 * surface is spacious where the case list is dense. Both are two settings of
 * one system, not two systems. */

export function DashboardPage() {
  const { t } = useTranslation()
  const format = useFormat()
  const navigate = useNavigate()
  const [creating, setCreating] = useState<'case' | null>(null)
  const { data: user } = useSession()
  const createCase = useCreateCase()
  /* No range selector while every range-sensitive figure is pending. The
     control returns with the tiles it filters, and with the shared date-range
     component Story 6.3 introduces — a picker that visibly changes nothing is
     worse than no picker. */
  const { data, isPending, isError, error, refetch } = useDashboard()

  const firstName = user?.name.split(' ')[0] ?? ''
  const now = new Date()

  /* The eyebrow states when the SERVER read the figures, not when this browser
     rendered them (NFR-2, UX-DR-16). The distinction is the whole criterion: a
     laptop resumed from sleep shows a cached response, and a browser-stamped
     time would date it "now" — the stale tab mistaken for a live one.

     Before the first response lands there is no server time to state, so the
     eyebrow falls back to the date alone rather than inventing one. */
  const header = (
    <PageHeader
      eyebrow={
        data
          ? t('dashboard.eyebrow', {
              date: format.dateLong(data.readAt.slice(0, 10)),
              time: format.time(data.readAt),
            })
          : format.dateLong(now.toISOString().slice(0, 10))
      }
      title={t(`dashboard.greeting.${greetingKey(now.getHours())}`, {
        name: firstName,
      })}
      subtitle={
        data ? t('dashboard.subtitle', { count: data.attention.length }) : undefined
      }
      actions={
        <>
          <Button
            variant="secondary"
            iconLeft={FileText}
            onClick={() => navigate('/quotes/new')}
          >
            {t('quotes.actions.newQuote')}
          </Button>
          <Button iconLeft={Plus} onClick={() => setCreating('case')}>
            {t('cases.actions.newCase')}
          </Button>
        </>
      }
    />
  )

  /* The dashboard's create actions. A new QUOTE is a page, not a sheet — the
     server mints its number, so nothing can hang off it until it exists, and
     /quotes/new lands the user on the record itself — so only the case sheet is
     mounted here. It sits outside the loading and error branches below,
     so a create started from the greeting survives a background refetch.

     The sheet closes from its own onSuccess, never from onSubmit: closing on
     submit would discard what was typed the moment the server refused it, and
     the refusal would land on a sheet that was already gone (UX-DR-18). */
  const sheets = (
    <>
      <CaseFormSheet
        open={creating === 'case'}
        onClose={() => setCreating(null)}
        pending={createCase.isPending}
        onSubmit={(draft) =>
          createCase.mutate(toCasePayload(draft), {
            onSuccess: () => {
              setCreating(null)
              /* To the LIST, not to the case. The detail route is still served
                 by the showcase fixtures and 404s for every real id, so sending
                 the user onto the case they just opened showed "запись
                 недоступна" over a save that had in fact succeeded. The list is
                 real and the new case is in it. Becomes `/cases/${record.id}`
                 when the AD-23 read composer serves GET /cases/:id. */
              navigate('/cases')
            },
          })
        }
      />

      {/* A refused create renders beside the greeting rather than inside the
          sheet, which stays open still holding the draft: nothing typed is lost
          to a failed save (UX-DR-18). Part of `sheets` rather than a sibling so
          the loading and error branches below cannot render the sheets without
          the one surface that says why a save did not take. */}
      {createCase.error ? (
        <ErrorState error={createCase.error} onRetry={() => createCase.reset()} />
      ) : null}
    </>
  )

  if (isPending) {
    return (
      <>
        {header}
        <DashboardSkeleton />
        {sheets}
      </>
    )
  }

  if (isError) {
    return (
      <>
        {header}
        <ErrorState error={error} onRetry={() => void refetch()} />
        {sheets}
      </>
    )
  }

  // Money tiles are absent for roles without cross-case finance, exactly as the
  // Finance nav section is. The server withholds the data either way; this
  // keeps the layout honest rather than rendering three empty cards.
  const showMoney = user ? canSeeCrossCaseFinance(user.role) : false

  return (
    <>
      {header}

      <CounterStrip counters={data.counters} pending={data.pending} />

      {showMoney ? (
        <div className="grid items-start gap-3.5 lg:grid-cols-3">
          {MONEY_TILES.map((key) => {
            const story = storyFor(data.pending, key)
            /* The branch is on the DATA, never on the key: which tile carries a
               figure is the response's business, and a client that hard-coded
               "profit is real now" would have to be edited again the next time a
               story lands. `turnover` is the only key the server still declares
               pending, and it declares it rather than this file assuming it. */
            if (story) {
              return (
                <PendingTile
                  key={key}
                  label={t(`dashboard.tiles.${key}`)}
                  story={story}
                />
              )
            }
            return (
              <MoneyTile
                key={key}
                label={t(`dashboard.tiles.${key}`)}
                amount={figureFor(data.counters, key)}
                href={TILE_HREFS[key]}
                hrefLabel={t('dashboard.tiles.drillThrough')}
              />
            )
          })}
        </div>
      ) : null}

      <AttentionList items={data.attention} stuckDays={data.stuckDays} />

      {sheets}
    </>
  )
}
